// Wyszukiwarka produktów w Panelu Handlowca (z kontekstem klienta lub klasyczna /search/products)

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';

import { useAppNavigate } from 'hooks';
import { useGetClientProducts, useGetSearchProducts } from 'api';
import { useSelector } from 'store';
import { Link, Loader } from 'components/controls';
import { WiseSearch } from 'components/wise';
import type { IProductListItem } from 'api/types';

import styles from 'theme/components/layouts/MainLayout/components/HeaderTopBar/HeaderTopBar.module.scss';

function getProductDisplayInfo(product: IProductListItem) {
  const image =
    product.product_image_formatted_short_info?.images?.[0]?.thumb ??
    product.product_image_formatted_short_info?.images?.[0]?.min;
  const name =
    product.product_basic_short_info?.title ?? product.product_basic_full_info?.title ?? '';
  const unit =
    product.product_unit_short_info?.units?.[0]?.name ??
    product.product_price_short_info?.prices?.[0]?.unit_name ??
    '';
  const price =
    product.product_price_short_info?.prices?.[0]?.price_gross_formatted ??
    product.product_unit_short_info?.units?.[0]?.price_gross_formatted ??
    '';
  const currency = product.product_price_short_info?.prices?.[0]?.currency ?? '';
  return { image, name, unit, price, currency };
}

const TraderPanelProductSearch = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const currentClientId = useSelector((state) => state.traderDashboard.currentClientId);

  const [productSearchKeyword, setProductSearchKeyword] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasProductSearchKeyword = productSearchKeyword.trim().length > 0;

  const { data: clientProductsData, isFetching: isClientProductsFetching } = useGetClientProducts(
    currentClientId,
    productSearchKeyword
  );
  const { data: searchProductsData, isFetching: isSearchProductsFetching } = useGetSearchProducts(
    {
      search_keyword: productSearchKeyword,
      page: 1,
      limit: 10
    },
    {
      enabled: !currentClientId && hasProductSearchKeyword
    }
  );

  const products =
    currentClientId != null && currentClientId > 0
      ? clientProductsData?.items ?? []
      : searchProductsData?.items ?? [];
  const isProductsFetching =
    currentClientId != null && currentClientId > 0
      ? isClientProductsFetching
      : isSearchProductsFetching;
  const showProductsDropdown = hasProductSearchKeyword;

  useEffect(() => {
    if (!showProductsDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setProductSearchKeyword('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductsDropdown]);

  const handleSubmit = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    const query = `?search_keyword=${encodeURIComponent(trimmed)}`;
    if (currentClientId != null && currentClientId > 0) {
      navigate(`/trader/clients/${currentClientId}/products${query}`);
    } else {
      navigate(`/trader/products${query}`);
    }
    setProductSearchKeyword('');
  };

  const getProductLink = (product: IProductListItem) =>
    currentClientId != null && currentClientId > 0
      ? `/trader/clients/${currentClientId}/products/${product.id}`
      : `/trader/products/${product.id}`;

  return (
    <div
      className={styles.traderPanelRightSearch}
      ref={wrapperRef}
      style={{ position: 'relative' }}>
      <WiseSearch
        placeholder={t('Szukaj produktu')}
        value={productSearchKeyword}
        onChange={setProductSearchKeyword}
        debounced
        debounceTime={300}
        onSubmit={handleSubmit}
      />
      {showProductsDropdown && (
        <Paper elevation={3} className={styles.productsDropdown}>
          {isProductsFetching ? (
            <div className={styles.productsDropdownLoading}>
              <Loader />
            </div>
          ) : products.length === 0 ? (
            <div className={styles.productsDropdownEmpty}>{t('Brak wyników')}</div>
          ) : (
            <ul className={styles.productsDropdownList}>
              {products.map((product) => {
                const { image, name, unit, price, currency } = getProductDisplayInfo(product);
                return (
                  <li key={product.id}>
                    <Link
                      to={getProductLink(product)}
                      className={styles.productsDropdownItem}
                      onClick={() => setProductSearchKeyword('')}>
                      {image && (
                        <span className={styles.productsDropdownImage}>
                          <img src={image} alt="" />
                        </span>
                      )}
                      <span className={styles.productsDropdownContent}>
                        <span className={styles.productsDropdownName}>{name}</span>
                        <span className={styles.productsDropdownMeta}>
                          {unit && <span>{unit}</span>}
                          {price && (
                            <span className={styles.productsDropdownPrice}>
                              {price}
                              {currency && ` ${currency}`}
                            </span>
                          )}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Paper>
      )}
    </div>
  );
};

export default TraderPanelProductSearch;
